// FolderTreePicker — a compact folder explorer used by CreateItemDialog
// for the "Parent folder" field. Renders the same tree the sidebar
// builds but folder-only (dashboards are noise when picking a parent)
// and inside a fixed-height scroll area so it slots into the dialog
// body without blowing it out vertically.
//
// Selection model: callers pass `value` as a path string (with "" for
// root) and get the new path back through `onChange`. Expansion state
// is owned locally and seeded so every ancestor of the initial value
// is open — the user can always see where their starting selection
// lives without having to click around.

import { useEffect, useMemo, useState } from 'react'
import { ChevronRight, Folder, FolderRoot } from 'lucide-react'

import { cn } from '../../lib/utils'
import { designPixelsToRem } from '../../lib/ui-density'
import { buildExplorerTree, type ExplorerTreeNode, type PathItem } from './tree'

export interface FolderTreePickerProps {
  folders: readonly PathItem[]
  /**
   * Items that live inside folders (dashboards or templates). Included so
   * the picker can surface "implicit" folders that exist only because an
   * item references their path. The picker itself never renders the
   * items — they are consumed only for their `path` strings.
   */
  dashboards: readonly PathItem[]
  /** Selected folder path. "" means root. */
  value: string
  onChange: (path: string) => void
  /** Optional dom id so a `<label htmlFor>` can target the picker. */
  id?: string
  disabled?: boolean
  /**
   * Folder paths that cannot be selected (the source folder and its
   * descendants when moving a folder). Root (`""`) may also be listed.
   */
  disabledPaths?: ReadonlySet<string>
}

interface FolderRowProps {
  node: ExplorerTreeNode<PathItem, PathItem>
  depth: number
  expanded: Set<string>
  onToggle: (path: string) => void
  value: string
  onChange: (path: string) => void
  disabled: boolean
  disabledPaths: ReadonlySet<string>
}

const FolderRow = ({
  node,
  depth,
  expanded,
  onToggle,
  value,
  onChange,
  disabled,
  disabledPaths
}: FolderRowProps) => {
  const childKeys = Object.keys(node.children)
  const hasChildren = childKeys.length > 0
  const isOpen = expanded.has(node.path)
  const isSelected = value === node.path
  const displayName = node.folder?.name ?? node.name
  const rowDisabled = disabled || disabledPaths.has(node.path)

  return (
    <div>
      <div
        role="treeitem"
        aria-expanded={hasChildren ? isOpen : undefined}
        aria-selected={isSelected}
        aria-disabled={rowDisabled || undefined}
        onClick={() => {
          if (rowDisabled) return
          onChange(node.path)
        }}
        className={cn(
          'flex items-center gap-1 rounded py-1 pr-1.5 font-mono text-xs',
          rowDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          isSelected ? 'bg-brand-soft text-ink' : 'text-ink-2 hover:bg-bg-sunk/60'
        )}
        style={{ paddingLeft: designPixelsToRem(6 + depth * 12) }}
      >
        {hasChildren ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled) onToggle(node.path)
            }}
            className="grid size-3.5 shrink-0 place-items-center rounded text-ink-3 hover:text-ink"
          >
            <ChevronRight
              size={10}
              className={cn('transition-transform duration-100', isOpen && 'rotate-90')}
              aria-hidden
            />
          </button>
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <Folder
          size={11}
          className={cn('shrink-0', isSelected ? 'text-brand' : 'text-ink-3')}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate">{displayName || '/'}</span>
      </div>
      {isOpen && hasChildren ? (
        <div role="group">
          {childKeys.map((k) => (
            <FolderRow
              key={node.children[k].path}
              node={node.children[k]}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              value={value}
              onChange={onChange}
              disabled={disabled}
              disabledPaths={disabledPaths}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Build the initial expanded set: every ancestor of `value` plus
 * `value` itself, so the user opens the dialog already looking at
 * their seeded selection.
 */
function ancestorsOf(path: string): Set<string> {
  const out = new Set<string>()
  if (path === '') return out
  const segments = path.split('/').filter(Boolean)
  let acc = ''
  for (const seg of segments) {
    acc = acc === '' ? seg : `${acc}/${seg}`
    out.add(acc)
  }
  return out
}

const FolderTreePicker = ({
  folders,
  dashboards,
  value,
  onChange,
  id,
  disabled = false,
  disabledPaths
}: FolderTreePickerProps) => {
  const tree = useMemo(
    () => buildExplorerTree<PathItem, PathItem>(folders, dashboards),
    [folders, dashboards]
  )
  const blocked = disabledPaths ?? new Set<string>()
  const childKeys = Object.keys(tree.children)
  const isRootSelected = value === ''
  const rootDisabled = disabled || blocked.has('')

  // Seed expansion from the initial `value` (and re-seed if it changes
  // between dialog opens). Subsequent toggles are user-driven.
  const [expanded, setExpanded] = useState<Set<string>>(() => ancestorsOf(value))
  useEffect(() => {
    setExpanded((prev) => {
      const merged = new Set(prev)
      for (const p of ancestorsOf(value)) merged.add(p)
      return merged
    })
  }, [value])

  const handleToggle = (path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  return (
    <div
      id={id}
      role="tree"
      aria-label="Parent folder"
      className={cn(
        'flex max-h-56 min-h-32 flex-col overflow-auto rounded-md border border-line bg-bg-elev px-1 py-1',
        disabled && 'pointer-events-none opacity-60'
      )}
    >
      <div
        role="treeitem"
        aria-selected={isRootSelected}
        aria-disabled={rootDisabled || undefined}
        onClick={() => {
          if (!rootDisabled) onChange('')
        }}
        className={cn(
          'flex items-center gap-1 rounded py-1 pr-1.5 pl-1.5 font-mono text-xs',
          rootDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          isRootSelected ? 'bg-brand-soft text-ink' : 'text-ink-2 hover:bg-bg-sunk/60'
        )}
      >
        <span className="w-3.5 shrink-0" />
        <FolderRoot
          size={11}
          className={cn('shrink-0', isRootSelected ? 'text-brand' : 'text-ink-3')}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate">/ (root)</span>
      </div>
      {childKeys.map((k) => (
        <FolderRow
          key={tree.children[k].path}
          node={tree.children[k]}
          depth={1}
          expanded={expanded}
          onToggle={handleToggle}
          value={value}
          onChange={onChange}
          disabled={disabled}
          disabledPaths={blocked}
        />
      ))}
    </div>
  )
}

export default FolderTreePicker
